import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { useAuth } from '@/shared/hooks/use-auth'
import { useUser } from '@/shared/hooks/use-user'
import { usePolicies } from '@/shared/hooks/queries/use-policies'
import { usePets } from '@/shared/hooks/queries/use-pets'
import { useRentersPolicy } from '@/shared/hooks/queries/use-renters-policy'
import { ROUTES } from '@/shared/constants/routes'
import {
  SESSION_STORAGE_KEY,
  SESSION_STORAGE_VALUE,
} from '@/shared/constants/storage'
import { TOAST_DISMISS_MS } from '@/shared/constants/timings'
import type { Policy, RentersPolicy } from '@/shared/types/global.types'
import type { PolicyCardProps } from '@/components/molecules/policy-card'

const INTERVAL_KEYS: Record<string, string> = {
  MONTHLY: 'payment.policies.monthly',
  QUARTERLY: 'payment.policies.quarterly',
  YEARLY: 'payment.policies.yearly',
}

const LOCALE_CURRENCY = 'en-US'

function formatCurrency(amount: number) {
  return new Intl.NumberFormat(LOCALE_CURRENCY, {
    style: 'currency',
    currency: 'USD',
  }).format(amount)
}

export type ActiveModal = 'payIn' | 'payOut' | undefined

export function usePaymentPage(initialModal: ActiveModal = undefined) {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuth()
  const { user, isLoading: userLoading } = useUser()
  const navigate = useNavigate()
  const [activeModal, setActiveModal] = useState<ActiveModal>(initialModal)
  const openPayInModal = useCallback(() => {
    navigate({ to: ROUTES.PAYMENT_IN_UPDATE })
  }, [navigate])
  const openPayOutModal = useCallback(() => {
    navigate({ to: ROUTES.PAYMENT_OUT_UPDATE })
  }, [navigate])
  const closeModal = useCallback(() => {
    if (activeModal === 'payIn' || activeModal === 'payOut') {
      navigate({ to: ROUTES.PAYMENT })
      return
    }
    setActiveModal(undefined)
  }, [activeModal, navigate])
  const handlePayOutSaveSuccess = useCallback(() => {
    navigate({ to: ROUTES.PAYMENT })
  }, [navigate])

  const [showSuccessToast, setShowSuccessToast] = useState(() => {
    const flag = sessionStorage.getItem(SESSION_STORAGE_KEY.PAYMENT_UPDATED)
    if (flag) sessionStorage.removeItem(SESSION_STORAGE_KEY.PAYMENT_UPDATED)
    return flag === SESSION_STORAGE_VALUE.TRUE
  })

  useEffect(() => {
    if (!showSuccessToast) return
    const timer = setTimeout(() => setShowSuccessToast(false), TOAST_DISMISS_MS)
    return () => clearTimeout(timer)
  }, [showSuccessToast])

  const isReady = isAuthenticated && !!user
  const policiesQuery = usePolicies(isReady)
  const petsQuery = usePets(isReady)
  const rentersPoliciesQuery = useRentersPolicy(isReady)

  const petMap = new Map((petsQuery.data ?? []).map((p) => [p.id, p]))

  const petPolicies: PolicyCardProps[] = (policiesQuery.data ?? []).map(
    (policy: Policy) => {
      const pet =
        policy.petId !== undefined
          ? (petMap.get(policy.petId) ?? undefined)
          : undefined
      const rawAmount =
        policy.payment?.invoiceInterval === 'YEARLY'
          ? policy.payment?.totalPayment
          : policy.payment?.recurringPayment
      return {
        id: policy.id,
        policyType: 'pet' as const,
        pet,
        displayName: pet?.name ?? `Policy ${policy.id ?? ''}`,
        displayAmount:
          rawAmount !== undefined ? formatCurrency(rawAmount) : undefined,
        displayIntervalKey: policy.payment?.invoiceInterval
          ? (INTERVAL_KEYS[policy.payment.invoiceInterval] ?? undefined)
          : undefined,
        isInArrears: policy.inArrears?.isInArrears ?? false,
        arrearsDisplay: policy.inArrears?.isInArrears
          ? {
              days: policy.inArrears.daysInArrears,
              amount: formatCurrency(policy.inArrears.amountInArrears),
            }
          : undefined,
      }
    },
  )

  const rentersPolicies: PolicyCardProps[] = (
    rentersPoliciesQuery.data ?? []
  ).map((policy: RentersPolicy) => {
    const rawAmount = policy.payment?.totalPayment
    const displayName = policy.dwellingType
      ? t(`onboarding.renters.dwellingTypes.${policy.dwellingType}`)
      : t('payment.policies.renters')
    return {
      id: policy.id,
      policyType: 'renters' as const,
      displayName,
      displayAmount:
        rawAmount !== undefined ? formatCurrency(rawAmount) : undefined,
      displayIntervalKey: policy.payment?.invoiceInterval
        ? (INTERVAL_KEYS[policy.payment.invoiceInterval] ?? undefined)
        : undefined,
      isInArrears: policy.inArrears?.isInArrears ?? false,
      arrearsDisplay: policy.inArrears?.isInArrears
        ? {
            days: policy.inArrears.daysInArrears ?? 0,
            amount: formatCurrency(policy.inArrears.amountInArrears ?? 0),
          }
        : undefined,
    }
  })

  const policies = [...petPolicies, ...rentersPolicies]

  const isOffline = !navigator.onLine

  return {
    user,
    loading: userLoading,
    policiesLoading:
      policiesQuery.isLoading ||
      petsQuery.isLoading ||
      rentersPoliciesQuery.isLoading,
    isOffline,
    policies,
    showSuccessToast,
    dismissToast: () => setShowSuccessToast(false),
    activeModal,
    openPayInModal,
    openPayOutModal,
    closeModal,
    handlePayOutSaveSuccess,
  }
}
